import React, { useEffect, useCallback, useState, useRef } from 'react';
import Select from 'react-select';
import Jspreadsheet from 'jspreadsheet-ce';
import './EditHistory.css';
import EditHistoryService from './EditHistoryService';
import CorgeeStorageHelper from '@/components/Globals/CorgeeStorageHelper';
import RenderInBody from '@components/Globals/RenderInBody';

import { useEditHistory, useDateManagement, useDashboard, useSpreadsheet } from './useEditHistory';
import type {
  CustomField,
  Conversation,
  PhaseSelected,
  Modal,
  SelectOption,
} from './EditHistory.types';

interface EditDetailsHistoryProps {
  customfield: CustomField;
  api: any;
  modal: Modal;
  conversation: Conversation;
  phaseSelected?: PhaseSelected;
  onclosemodal?: () => void;
  onSubmitTime?: () => void;
  title?: string;
  isDraggable?: boolean;
  closeOnEscape?: boolean;
  allowMinimize?: boolean;
  allowMaximize?: boolean;
  hasMaxHeight?: boolean;
  height?: number;
}

const EditDetailsHistory: React.FC<EditDetailsHistoryProps> = ({
  customfield,
  api,
  modal,
  conversation,
  phaseSelected,
  onclosemodal,
  onSubmitTime,
  title = 'Edit History',
  isDraggable: _isDraggable = false,
  closeOnEscape: _closeOnEscape = false,
  allowMinimize: _allowMinimize = false,
  allowMaximize: _allowMaximize = false,
  hasMaxHeight = false,
  height: _height = 800,
}) =>
{
  const {
    listValues: _listValues,
    listCustomFields: _listCustomFields,
    isSaving,
    noData,
    updateRowRef: _updateRowRef,
    cancelUpdateRef,
    lastValueRef,
    setListValues,
    setListCustomFields,
    setIsSaving: _setIsSaving,
    setNoData,
    addValue: _addValue,
    updateCustomFieldValue: _updateCustomFieldValue,
  } = useEditHistory();

  const {
    selectMonth,
    selectYear,
    setSelectMonth,
    setSelectYear,
    getMonthOptions,
    getRangeDates,
    getIsToday,
    formatDate,
  } = useDateManagement();

  const {
    dashboards: _dashboards,
    selectDashboard,
    showErrorDashboard,
    setDashboards,
    setSelectDashboard,
    setShowErrorDashboard,
  } = useDashboard();

  const { wrapperRef, spreadsheetRef, setOptions, updateDimensions } = useSpreadsheet();

  const listCustomFieldsRef = useRef<any[]>([]);
  const listValuesRef = useRef<any[]>([]);
  const isSavingInlineRef = useRef(false);
  const onSubmitTimeRef = useRef(onSubmitTime);

  useEffect(() =>
  {
    onSubmitTimeRef.current = onSubmitTime;
  }, [onSubmitTime]);

  // Counter to trigger jspreadsheet init regardless of noData transition
  const [spreadsheetKey, setSpreadsheetKey] = useState(0);

  // Refs to avoid stale closures in jspreadsheet callbacks and break dep cycles
  const selectDashboardRef = useRef('');
  const pendingOptionsRef = useRef<any>(null); // options waiting for wrapper to be rendered

  useEffect(() =>
  {
    selectDashboardRef.current = selectDashboard;
  }, [selectDashboard]);

  const getLoaderUrl = (): string =>
  {
    const url = document.URL;
    if (url.indexOf('/Chat/') > 0) return '../../../Images/loader.gif';
    if (url.indexOf('ConversationDetails') > 0 || url.indexOf('chat') > 0)
      return '../../Images/loader.gif';
    return 'Images/loader.gif';
  };

  // Initialize jspreadsheet after loadData signals readiness via spreadsheetKey increment
  useEffect(() =>
  {
    if (!pendingOptionsRef.current) return;
    const opts = pendingOptionsRef.current;
    pendingOptionsRef.current = null;

    if (!wrapperRef.current) return;
    Jspreadsheet.destroy(wrapperRef.current as any, false);
    spreadsheetRef.current = Jspreadsheet(wrapperRef.current as any, opts);
    // v5 returns WorksheetInstance directly; worksheets[] is on SpreadsheetInstance
    const ws = spreadsheetRef.current.worksheets?.[0] ?? spreadsheetRef.current;
    ws.hideIndex?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadsheetKey]);

  // loadData — fully parameterized: no selectDashboard/componentHeight/componentWidth in deps
  const loadData = useCallback(
    async (startReal: string, endReal: string, dashboardId: string) =>
    {
      let conversationId = customfield.ConversationId;
      let hasPhases = conversation.HasPhases;

      if (phaseSelected?.ConversationPhaseId)
      {
        conversationId = phaseSelected.ConversationPhaseId;
        hasPhases = false;
      }

      if (!dashboardId)
      {
        setShowErrorDashboard(true);
        setNoData(true);
        updateDimensions(445, 120);
        return;
      }

      const request = {
        api,
        ConversationId: conversationId,
        Start: startReal,
        End: endReal,
      };

      const ranges = getRangeDates(startReal, endReal).reverse();

      try
      {
        const data = await EditHistoryService.getListMultipleCustomFieldHistories(request);

        setListValues(data.ListValues);
        setListCustomFields(data.ListCustomFields as any);
        listCustomFieldsRef.current = data.ListCustomFields as any[];
        listValuesRef.current = data.ListValues as any[];

        if (data.ListCustomFields.length === 0)
        {
          setNoData(true);
          updateDimensions(250, 200);
          return;
        }

        // Build columns
        const columns = [
          {
            type: 'hidden',
            title: 'CustomFieldId',
            width: 90,
            readOnly: true,
            name: 'CustomFieldId',
          },
          {
            type: 'hidden',
            title: 'CustomFieldValueId',
            width: 90,
            readOnly: true,
            name: 'CustomFieldValueId',
          },
          { type: 'hidden', title: 'FieldTypeId', width: 90, name: 'FieldTypeId' },
          { type: 'hidden', title: 'LayerId', width: 90, readOnly: true, name: 'LayerId' },
          { type: 'text', title: 'Layers', width: 90, readOnly: true, name: 'Layers' },
          { type: 'text', title: 'Detail', width: 90, readOnly: true, name: 'Detail' },
          ...ranges.map(range => ({
            type: 'text',
            title: range,
            width: 90,
            readOnly: false,
            name: range,
          })),
        ];

        // Build data rows
        const dataRows = data.ListCustomFields.map(field =>
        {
          const row: any = {
            CustomFieldId: field.CustomFieldId,
            CustomFieldValueId: field.CustomFieldValueId,
            FieldTypeId: field.FieldTypeId,
            LayerId: field.LayerId,
            Layers: field.LayerName,
            Detail: field.Name,
          };
          ranges.forEach(range =>
          {
            row[range] = '';
          });
          data.ListValues.forEach(value =>
          {
            if (value.LayerId === field.LayerId && value.Name === field.Name)
            {
              const valueDate = new Date(value.CreatedAt.replace('Z', ''));
              const dateKey = formatDate(valueDate);
              const currentValue = parseFloat(value.Value || '0');
              const existingValue = row[dateKey] ? parseFloat(row[dateKey]) : 0;
              row[dateKey] = (existingValue + currentValue).toString();
            }
          });
          return row;
        });

        // Calculate dimensions
        const windowsH = window.innerHeight - 100;
        const sizeH = data.ListCustomFields.length * 22 + 50;
        const heightW = Math.min(sizeH, windowsH - 180);
        const windowsW = window.innerWidth - 500;
        const sizeW = ranges.length * 90 + 200;
        const widthW = Math.min(sizeW, windowsW - 70);

        // jspreadsheet-ce v5: event handlers at SpreadsheetOptions level,
        // data/column options inside worksheets[]
        const newOptions: any = {
          contextMenu: false,
          onbeforechange: (instance: any, _cell: any, x: number, y: number) =>
          {
            if (instance.getSelectedRows().length > 1 || instance.getSelectedColumns().length > 1)
            {
              cancelUpdateRef.current = true;
              lastValueRef.current = instance.getValueFromCoords(x, y);
            }
          },
          onchange: (instance: any, _cell: any, x: number, y: number, value: string) =>
          {
            // Date columns start at x >= 6 (cols 0-5 are CustomFieldId, CustomFieldValueId,
            // FieldTypeId, LayerId, Layers, Detail — all hidden or readOnly)
            if (x < 6) return;
            if (isSavingInlineRef.current) return;

            // jspreadsheet-ce v5 setData converts object rows to arrays internally,
            // so getRowData(y) returns an array (not the original object).
            // Convert back to a named object using column definitions.
            const colNames = columns.map((c: any) => c.name);
            const rawRow: any[] = (instance.getRowData(y) ?? []) as any[];
            const rowData: any = {};
            colNames.forEach((k: string, i: number) =>
            {
              rowData[k] = rawRow[i];
            });

            const cellName: string = instance.getHeader(x); // date "MM/DD/YYYY"

            // rowToArray: convert named object back to array ordered by column for setRowData
            const rowToArray = (r: any): any[] => colNames.map((k: string) => r[k] ?? null);

            if (cancelUpdateRef.current)
            {
              cancelUpdateRef.current = false;
              rowData[cellName] = lastValueRef.current;
              instance.ignoreEvents = true;
              instance.setRowData(y, rowToArray(rowData));
              instance.ignoreEvents = false;
              return;
            }

            instance.resetSelection();
            instance.ignoreEvents = true;

            const elementGrid = wrapperRef.current;
            if (!elementGrid) return;

            // Find matching CustomField by LayerId + Detail(Name) + CustomFieldId
            const listCFs = listCustomFieldsRef.current;
            const cfIdx = listCFs.findIndex(
              (f: any) =>
                f.LayerId === rowData.LayerId &&
                f.Name === rowData.Detail &&
                f.CustomFieldId === rowData.CustomFieldId,
            );
            if (cfIdx === -1)
            {
              instance.ignoreEvents = false;
              return;
            }

            const cf = listCFs[cfIdx];
            const objCustomField = {
              CoreTaskID: cf.CoreTaskId,
              ProjectId: cf.ProjectId ?? 0,
              PersonId: cf.PersonId ?? 0,
              CustomFieldId: cf.CustomFieldId,
              CustomFieldValueId: cf.CustomFieldValueId ?? 0,
              Description: cf.Description ?? cf.Name,
              FieldTypeId: cf.FieldTypeId,
              LayerId: cf.LayerId,
              Name: cf.Name,
              Value: value,
            };

            // Find customFieldDashboardId from listValues by LayerId + Name + date
            let customFieldDashboardId = 0;
            for (const v of listValuesRef.current)
            {
              const vDate = new Date(v.CreatedAt.replace('Z', ''));
              const vDateStr = formatDate(vDate);
              if (
                v.LayerId === rowData.LayerId &&
                v.Name === rowData.Detail &&
                vDateStr === cellName
              )
              {
                customFieldDashboardId = v.CustomFieldDashboardId;
                break;
              }
            }

            // Validate numeric
            if (value !== '')
            {
              if (cf.FieldTypeId === 3 && String(value).search(/^\d+(\.\d+)?$/) === -1)
              {
                rowData[cellName] = '';
                instance.ignoreEvents = true;
                instance.setRowData(y, rowToArray(rowData));
                instance.ignoreEvents = false;
                return;
              }
              if (cf.FieldTypeId === 4 && String(value).search(/^\d+$/) === -1)
              {
                rowData[cellName] = '';
                instance.ignoreEvents = true;
                instance.setRowData(y, rowToArray(rowData));
                instance.ignoreEvents = false;
                return;
              }
            }

            const isToday = getIsToday(cellName);
            elementGrid.style.opacity = '0.3';
            isSavingInlineRef.current = true;

            const finalize = () =>
            {
              instance.ignoreEvents = true;
              instance.setRowData(y, rowToArray(rowData));
              instance.ignoreEvents = false;
              instance.resetSelection();
              elementGrid.style.opacity = '1';
              isSavingInlineRef.current = false;
              onSubmitTimeRef.current?.();
            };

            const handleError = () =>
            {
              elementGrid.style.opacity = '1';
              isSavingInlineRef.current = false;
              instance.ignoreEvents = false;
            };

            // After savePost/save: POST NewCustomFieldHistories (= legacy updateCustomDetailDashboard)
            const runNewHistory = (customFieldValueId: number): Promise<void> =>
            {
              const create = new Date(cellName);
              create.setHours(0, 0, 0, 0);
              return EditHistoryService.newCustomFieldHistories({
                api,
                request: {
                  CreatedAt: create.toISOString().replace('Z', ''),
                  CustomFieldDashboardId: customFieldDashboardId,
                  Value: value,
                  CustomFieldValueId: customFieldValueId,
                  CustomFieldId: objCustomField.CustomFieldId,
                  CoreTaskId: objCustomField.CoreTaskID,
                  DashboardId: selectDashboardRef.current,
                },
              })
                .then(resp =>
                {
                  listValuesRef.current.push({
                    CustomFieldDashboardId: resp.CustomFieldDashboardId,
                    CustomFieldId: objCustomField.CustomFieldId,
                    CoreTaskId: objCustomField.CoreTaskID,
                    Name: objCustomField.Name,
                    LayerId: rowData.LayerId,
                    CreatedAt: cellName,
                    Value: value,
                    Last: 0,
                  });
                  finalize();
                })
                .catch(handleError);
            };

            const runSaveAndHistory = () =>
            {
              if (!objCustomField.CustomFieldValueId)
              {
                EditHistoryService.savePost({ api, request: objCustomField })
                  .then(data =>
                  {
                    listCFs[cfIdx].CustomFieldValueId = data.CustomFieldValueId;
                    return runNewHistory(data.CustomFieldValueId);
                  })
                  .catch(handleError);
              }
              else
              {
                EditHistoryService.save({ api, request: objCustomField })
                  .then(() => runNewHistory(objCustomField.CustomFieldValueId))
                  .catch(handleError);
              }
            };

            if (customFieldDashboardId > 0)
            {
              // Existing dashboard entry: always update, and if today also save + new history
              EditHistoryService.updateCustomFieldHistories({
                api,
                request: { CustomFieldDashboardId: customFieldDashboardId, Value: value },
              })
                .then(() =>
                {
                  if (isToday)
                  {
                    runSaveAndHistory();
                  }
                  else
                  {
                    finalize();
                  }
                })
                .catch(handleError);
            }
            else
            {
              // No existing dashboard entry
              if (isToday)
              {
                runSaveAndHistory();
              }
              else
              {
                // Past column: require CustomFieldValueId (must log today first)
                if (!objCustomField.CustomFieldValueId)
                {
                  rowData[cellName] = '';
                  instance.ignoreEvents = true;
                  instance.setRowData(y, rowToArray(rowData));
                  instance.ignoreEvents = false;
                  elementGrid.style.opacity = '1';
                  isSavingInlineRef.current = false;
                  alert('Please log the hours backwards, starting from the current day.');
                  return;
                }
                const create = new Date(cellName);
                create.setHours(0, 0, 0, 0);
                EditHistoryService.newCustomFieldHistories({
                  api,
                  request: {
                    CreatedAt: create.toISOString().replace('Z', ''),
                    CustomFieldDashboardId: 0,
                    Value: value,
                    CustomFieldValueId: objCustomField.CustomFieldValueId,
                    CustomFieldId: objCustomField.CustomFieldId,
                    CoreTaskId: objCustomField.CoreTaskID,
                    DashboardId: selectDashboardRef.current,
                  },
                })
                  .then(resp =>
                  {
                    listValuesRef.current.push({
                      CustomFieldDashboardId: resp.CustomFieldDashboardId,
                      CustomFieldId: objCustomField.CustomFieldId,
                      CoreTaskId: objCustomField.CoreTaskID,
                      Name: objCustomField.Name,
                      LayerId: rowData.LayerId,
                      CreatedAt: cellName,
                      Value: value,
                      Last: 0,
                    });
                    finalize();
                  })
                  .catch(handleError);
              }
            }
          },
          worksheets: [
            {
              columnDrag: false,
              data: dataRows,
              columns,
              columnResize: false,
              rowResize: false,
              rowDrag: false,
              columnSorting: false,
              editable: true,
              allowInsertRow: false,
              updateTable: (el: any, cell: any, x: number, y: number) =>
              {
                if (x > 4)
                {
                  // jspreadsheet-ce v5 stores rows as arrays — index 5 is 'Detail'
                  const rawRow: any[] = (el.getRowData(y) ?? []) as any[];
                  const detailValue: string = rawRow[5] ?? '';
                  if (hasPhases && ['Estimated', 'Remaining', 'Actual'].includes(detailValue))
                  {
                    cell.classList.add('readonly');
                  }
                  if (
                    detailValue === 'Actual' &&
                    !(
                      CorgeeStorageHelper.isAdmin() ||
                      CorgeeStorageHelper.canEditOwner(
                        conversation,
                        CorgeeStorageHelper.personId(),
                        true,
                      )
                    )
                  )
                  {
                    cell.classList.add('readonly');
                  }
                }
              },
            },
          ],
        };

        setOptions(newOptions);
        updateDimensions(widthW + 50, heightW + 10);

        // Store options then increment key — useEffect fires after render with wrapper in DOM
        pendingOptionsRef.current = newOptions;
        setNoData(false);
        setSpreadsheetKey(k => k + 1);
      }
      catch (error)
      {
        console.error('Error loading data:', error);
        setNoData(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      api,
      customfield,
      conversation,
      phaseSelected,
      getRangeDates,
      formatDate,
      getIsToday,
      setListValues,
      setListCustomFields,
      setNoData,
      setOptions,
      updateDimensions,
      setSpreadsheetKey,
      cancelUpdateRef,
      lastValueRef,
    ],
  );

  // Initialize once when modal opens — no loadData in deps to avoid loops
  useEffect(() =>
  {
    if (!modal.open) return;

    let cancelled = false;

    const run = async () =>
    {
      let coreTaskId = conversation.CoreTaskId;
      if (phaseSelected?.ConversationPhaseId) coreTaskId = phaseSelected.CoreTaskId;

      try
      {
        const dashboardData = await EditHistoryService.getDashboardsByCoreTaskId({
          api,
          CoreTaskId: coreTaskId,
        });
        if (cancelled) return;

        const dashboardOptions: SelectOption[] = dashboardData.map((item: any) => ({
          value: item.DashboardId,
          label: item.Name,
        }));
        setDashboards(dashboardOptions);

        const dashboardId = dashboardData.length > 0 ? dashboardData[0]!.DashboardId : '';
        setSelectDashboard(dashboardId);
        selectDashboardRef.current = dashboardId;

        const now = new Date();
        setSelectMonth(now.getMonth().toString());
        setSelectYear(now.getFullYear());

        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const startReal = formatDate(start);
        const endReal = formatDate(now);

        if (!cancelled)
        {
          await loadData(startReal, endReal, dashboardId);
        }
      }
      catch (error)
      {
        if (!cancelled) console.error('Error initializing:', error);
      }
    };

    run();
    return () =>
    {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.open]);

  const handleCloseModal = useCallback(() =>
  {
    onclosemodal?.();
  }, [onclosemodal]);

  const handleMonthChange = useCallback(
    (option: SelectOption | null) =>
    {
      if (option) setSelectMonth(option.value);
    },
    [setSelectMonth],
  );

  const handleYearChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
    {
      const val = parseInt(event.target.value);
      setSelectYear(Math.min(val, new Date().getFullYear()));
    },
    [setSelectYear],
  );

  const handleSearch = useCallback(() =>
  {
    const now = new Date();
    const start = new Date(selectYear, parseInt(selectMonth), 1);
    const isCurrentMonth =
      now.getMonth() === parseInt(selectMonth) && now.getFullYear() === selectYear;
    const endDate = isCurrentMonth ? now : new Date(selectYear, parseInt(selectMonth) + 1, 0);
    const startReal = formatDate(start);
    const endReal = formatDate(endDate);
    loadData(startReal, endReal, selectDashboardRef.current);
  }, [selectMonth, selectYear, formatDate, loadData]);

  const stepUpYear = useCallback(() =>
  {
    setSelectYear(prev => Math.min(prev + 1, new Date().getFullYear()));
  }, [setSelectYear]);

  const stepDownYear = useCallback(() =>
  {
    setSelectYear(prev => Math.max(prev - 1, 2000));
  }, [setSelectYear]);

  const monthOptions = getMonthOptions();
  const loaderUrl = getLoaderUrl();

  return (
    <div className="edit-history-multiple">
      {modal.open && (
        <RenderInBody>
          <div
            className="edit-history-multiple corgee_com_modal-backdrop"
            onClick={handleCloseModal}
          >
            <div
              className="corgee_com_conversation-metadata-modal"
              style={{ width: '1100px', maxWidth: '95vw' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="corgee_com_global-modal_header">
                <div className="header-info">
                  <div className="react-add-new-card-title">{title}</div>
                  <button className="close-control" onClick={handleCloseModal}>
                    <i className="icon-incorrect-icon"></i>
                  </button>
                </div>
              </div>
              <div>
                <p className="titleEditHistory">
                  *Please log the hours backwards, starting from the current day.
                </p>
              </div>
              <div
                className={
                  hasMaxHeight
                    ? 'max-heigth-eh corgee_com_global-modal_body'
                    : 'corgee_com_global-modal_body'
                }
                style={{ overflowX: 'auto', overflowY: 'hidden' }}
              >
                <div id="edit-history-container" className="containerHistory">
                  <div className="headerMultipleHistory">
                    <div className="rowh">
                      <div className="pad">
                        <div className="cmbSelectMonthEdit">
                          <Select
                            classNamePrefix="eh-select"
                            value={monthOptions.find(opt => opt.value === selectMonth)}
                            options={monthOptions}
                            onChange={handleMonthChange}
                          />
                        </div>
                      </div>
                      <div className="pad">
                        <div className="quantity">
                          <input
                            id="inputYear"
                            name="inputYear"
                            type="number"
                            min="2000"
                            max="2100"
                            value={selectYear}
                            onChange={handleYearChange}
                          />
                          <div className="quantity-nav">
                            <div className="quantity-button quantity-up" onClick={stepUpYear}>
                              ▲
                            </div>
                            <div className="quantity-button quantity-down" onClick={stepDownYear}>
                              ▼
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="pad"></div>
                      <div className="pad">
                        <input
                          id="btnSearch"
                          name="btnSearch"
                          type="button"
                          value="Search"
                          onClick={handleSearch}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bodyHistory">
                    {noData ? (
                      <div
                        style={{
                          width: '100%',
                          textAlign: 'center',
                          height: '180px',
                          overflow: 'hidden',
                        }}
                      >
                        <div style={{ height: '100%', transform: 'translateY(45%)' }}>
                          {showErrorDashboard
                            ? "You don't have the card on a dashboard. Cards in dynamic column do not apply."
                            : 'No data available'}
                        </div>
                      </div>
                    ) : isSaving ? (
                      <div>
                        <img className="loaderImageEditDetail" src={loaderUrl} alt="Loading..." />
                        <div ref={wrapperRef} id="wrapper" />
                      </div>
                    ) : (
                      <div ref={wrapperRef} id="wrapper" />
                    )}
                  </div>
                </div>
              </div>

              <div className="corgee_com_global-modal_footer">
                <input
                  className="btn-close-history"
                  type="button"
                  value="Close"
                  onClick={handleCloseModal}
                />
              </div>
            </div>
          </div>
        </RenderInBody>
      )}
    </div>
  );
};

export default EditDetailsHistory;
